import type { IonInfiniteScrollCustomEvent } from "@ionic/core";
import {
  IonButton,
  IonContent,
  IonFab,
  IonIcon,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonNote,
  IonText,
} from "@ionic/react";
import { arrowUp } from "ionicons/icons";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Subscription } from "stompjs";
import { send, stompClient } from "../../utils/sockets/asyncSocket";
import config from "../helpers/socket/config";
import { pubsubEvents } from "../helpers/socket/pubsubEvent";
import { Block } from "./Block";
import "./blockFeed.css";
import { BlockTrade } from "./type";
import { v4 as uuidv4 } from "uuid";

import {
  BLOCK_FEED_ACTION,
  BLOCK_FEED_DELETE_LIVE_TOPIC,
  BLOCK_FEED_LIVE_TOPIC,
} from "./constants";
import { Skeleton } from "./Skeleton";
import AppPrefContext from "../contexts/AppPrefContext";

type BlockFeedDetailedProps = {
  assetClassActiveFilters: Record<string, Set<string>>;
  blockContentActiveFilter: Set<string>;
  exchangesFilter: Set<string>;
  selectedDate: string | undefined;
};

const DetailedBlockFeed: React.FC<BlockFeedDetailedProps> = ({
  assetClassActiveFilters,
  blockContentActiveFilter,
  exchangesFilter,
  selectedDate,
}) => {
  const [items, setItems] = useState<BlockTrade[]>([]);
  const [blockState, setBlockState] = useState<{
    filteredItems: BlockTrade[];
    isLoading: boolean;
  }>({ filteredItems: [], isLoading: true });
  const [hasLatestItems, setHasLatestItems] = useState<boolean>(false);
  const [newLiveItems, setNewLiveItems] = useState<BlockTrade[]>([]);
  const [isHistoricalFetchActive, setIsHistoricalFetchActive] = useState(false);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const [hasHistoricalData, setHasHistoricalData] = useState(true);
  const [operationOrder, setOperationOrder] = useState<any[]>([]);

  const appPref = useContext(AppPrefContext);

  const contentRef = useRef<HTMLIonContentElement>(null);

  console.log(`IsActive: ${appPref?.isAppActive}`);

  const resetBlocks = () => {
    setItems([]);
    setHasHistoricalData(true);
    setHasLatestItems(false);
    setOperationOrder([]);
    setNewLiveItems([]);
    if (contentRef) {
      contentRef.current?.scrollToTop(0);
    }
  };

  const processAssetClassFilter = useCallback(
    (blocks: BlockTrade[]) => {
      if (
        assetClassActiveFilters == null ||
        Object.keys(assetClassActiveFilters).length === 0
      ) {
        return blocks;
      }

      const parsedFilters = Object.entries(assetClassActiveFilters).map(
        ([filterCategory, allowedValues]) => {
          const category = filterCategory.includes("-")
            ? filterCategory.split("-")[0]
            : filterCategory;

          return {
            category,
            allowedValues,
            hasAllowedValues: allowedValues != null && allowedValues.size > 0,
          };
        },
      );

      return blocks.filter((block) => {
        if (!block.legs) return false;

        return parsedFilters.some(
          ({ category, allowedValues, hasAllowedValues }) => {
            return block.legs.some(({ metadata }) => {
              if (!metadata) return false;

              const { astSubClas, webAstSubClas } = metadata;

              return hasAllowedValues
                ? astSubClas === category && allowedValues.has(webAstSubClas)
                : webAstSubClas === category;
            });
          },
        );
      });
    },
    [assetClassActiveFilters],
  );

  const processBlockContentFilter = useCallback(
    (blocks: BlockTrade[]) => {
      if (!blockContentActiveFilter || blockContentActiveFilter.size === 0) {
        return blocks;
      }

      const includeOptions = blockContentActiveFilter.has("OPT");

      return blocks.filter((block) => {
        const strategyMatch =
          typeof block.strategyType === "string" &&
          blockContentActiveFilter.has(block.strategyType);

        if (strategyMatch) return true;

        if (!block.legs) return false;

        return block.legs.some((leg) => {
          const legType = leg?.metadata?.type;
          if (!legType) return false;

          if (includeOptions && legType !== "FUT") {
            return true;
          }
          return blockContentActiveFilter.has(legType);
        });
      });
    },
    [blockContentActiveFilter],
  );

  const processExchangeFilter = useCallback(
    (blocks: BlockTrade[]) => {
      if (!exchangesFilter || exchangesFilter.size === 0) {
        return blocks;
      }

      return blocks.filter((block) => {
        if (!block.legs) return false;

        return block.legs.some((leg) => {
          const div = leg?.metadata?.div;
          return div != null && exchangesFilter.has(div);
        });
      });
    },
    [exchangesFilter],
  );

  const latestFiltersRef = useRef({
    processAssetClassFilter,
    processBlockContentFilter,
    processExchangeFilter,
  });

  useEffect(() => {
    latestFiltersRef.current = {
      processAssetClassFilter,
      processBlockContentFilter,
      processExchangeFilter,
    };
  }, [
    processAssetClassFilter,
    processBlockContentFilter,
    processExchangeFilter,
  ]);

  const handlePopupClick = () => {
    if (operationOrder.length === 0) {
      return;
    }
    setItems((items) => {
      let localItems = [...items];
      for (let i = 0; i < operationOrder.length; i++) {
        const operation = operationOrder[i];
        if (
          operation.delete != null &&
          Array.isArray(operation.delete) &&
          operation.delete.length > 0 &&
          operation.delete[0] === -1
        ) {
          localItems = [];
        } else if (
          operation.delete != null &&
          Array.isArray(operation.delete) &&
          operation.delete.length > 0
        ) {
          for (let j = 0; j < operation.delete.length; j++) {
            const index = localItems.findIndex(
              (localItem) => localItem.id === operation.delete[j],
            );
            localItems.splice(index, 1);
          }
        } else if (operation.add != null) {
          const id = operation.add;
          const isExist = localItems.some((litem) => litem.id === id);
          const newItem = newLiveItems.find((newItem) => newItem.id === id);
          if (!isExist && newItem) {
            localItems.unshift(newItem);
          }
        }
      }
      return localItems;
    });

    setNewLiveItems([]);
    contentRef.current?.scrollToTop(300);
    setHasLatestItems(false);
    setOperationOrder([]);
  };

  useEffect(() => {
    resetBlocks();
    setIsInitialLoadComplete(false);
    setBlockState((state) => ({
      ...state,
      isLoading: true,
    }));

    if (!appPref?.isAppActive) {
      return;
    }

    let timer: NodeJS.Timeout | undefined;
    PubSub.subscribe(
      pubsubEvents.stompSubscriptionKeys.Block_FEED_HISTORICAL_DATA,
      (_, res) => {
        setItems((items) =>
          items != null ? [...items, ...res.payload] : [...res.payload],
        );
        setBlockState((state) => ({
          ...state,
          isLoading: false,
        }));

        if (res.payload.length === 0) {
          setHasHistoricalData(false);
        }
        setIsHistoricalFetchActive(false);
        timer = setTimeout(() => {
          setIsInitialLoadComplete(true);
        }, 100);
      },
    );

    const request = {
      action: BLOCK_FEED_ACTION,
      requestID: uuidv4(),
      payload: {
        blockSize: 50,
        dealDate: selectedDate,
      },
      type: "notUsedButMustBePresentForLegacyValidation",
    };
    send(config.socket.channels.CORE, request);

    return () => {
      PubSub.unsubscribe(
        pubsubEvents.stompSubscriptionKeys.Block_FEED_HISTORICAL_DATA,
      );
      timer != null && clearTimeout(timer);
    };
  }, [selectedDate, appPref?.isAppActive]);

  useEffect(() => {
    let subscription: Subscription | undefined;
    let deleteSubscription: Subscription | undefined;

    if (!appPref?.isAppActive) {
      setNewLiveItems([]);
      setHasLatestItems(false);
      setOperationOrder([]);
      subscription?.unsubscribe();
      deleteSubscription?.unsubscribe();
      return;
    }

    if (selectedDate === "topday") {
      subscription = stompClient?.subscribe(
        BLOCK_FEED_LIVE_TOPIC,
        (message: any) => {
          if (message.body) {
            const payload = JSON.parse(message.body);
            const {
              processAssetClassFilter,
              processBlockContentFilter,
              processExchangeFilter,
            } = latestFiltersRef.current;
            setOperationOrder((prev) => {
              return [...prev, { add: payload.id }];
            });

            setNewLiveItems((current) => {
              const newLatest =
                current == null ? [payload, ...current] : [payload];
              let newLatestAfterFilter = processAssetClassFilter(newLatest);
              newLatestAfterFilter =
                processBlockContentFilter(newLatestAfterFilter);
              newLatestAfterFilter =
                processExchangeFilter(newLatestAfterFilter);
              if (newLatestAfterFilter.length > 0) {
                console.log("newLatestAfterFilter");
                console.log(newLatestAfterFilter);
                setHasLatestItems(true);
              }
              return newLatest;
            });
          }
        },
      );
      deleteSubscription = stompClient?.subscribe(
        BLOCK_FEED_DELETE_LIVE_TOPIC,
        (message: any) => {
          if (message.body) {
            const payload = JSON.parse(message.body);

            console.log(payload);
            if (payload?.delete?.[0] === -1) {
              setOperationOrder((prev) => {
                return [...prev, { delete: [-1] }];
              });
            } else if (payload?.delete?.length != 0) {
              setOperationOrder((prev) => {
                return [...prev, { delete: payload?.delete }];
              });
            }
            setHasLatestItems(true);
          }
        },
      );
    } else {
      subscription?.unsubscribe();
      deleteSubscription?.unsubscribe();
    }

    return () => {
      subscription?.unsubscribe();
      deleteSubscription?.unsubscribe();
      console.log("unsubscribed from live messages");
    };
  }, [selectedDate, appPref?.isAppActive]);

  const getBlocksForTimestamp = (
    event?: IonInfiniteScrollCustomEvent<void>,
  ) => {
    if (
      isHistoricalFetchActive ||
      items == null ||
      items.length === 0 ||
      !hasHistoricalData
    ) {
      event?.target?.complete();
      return;
    }
    setIsHistoricalFetchActive(true);
    const request = {
      action: BLOCK_FEED_ACTION,
      requestID: uuidv4(),
      payload: {
        blockSize: 50,
        dealDate: selectedDate,
        dealTime: items[items.length - 1].id,
      },
      type: "notUsedButMustBePresentForLegacyValidation",
    };
    send(config.socket.channels.CORE, request);
    setTimeout(() => {
      event?.target?.complete();
    }, 500);
  };

  useEffect(() => {
    // update items on filters change to get all the items available
    handlePopupClick();
  }, [assetClassActiveFilters, blockContentActiveFilter, exchangesFilter]);

  useEffect(() => {
    const applyFilters = () => {
      let filteredItems: BlockTrade[];

      filteredItems = processAssetClassFilter(items);
      filteredItems = processBlockContentFilter(filteredItems);
      filteredItems = processExchangeFilter(filteredItems);

      if (
        filteredItems.length == 0 ||
        (filteredItems.length < 50 && hasHistoricalData)
      ) {
        getBlocksForTimestamp();
      }
      setBlockState((prev) => {
        return {
          ...prev,
          filteredItems: filteredItems,
        };
      });
    };
    applyFilters();
  }, [
    items,
    processAssetClassFilter,
    processBlockContentFilter,
    processExchangeFilter,
    hasHistoricalData,
  ]);

  const resetFlagOnScrollingTop = (e: CustomEvent) => {
    const scrollTop = e.detail.scrollTop;
    if (scrollTop === 0 && hasLatestItems) {
      setItems((existing) => [...newLiveItems, ...existing]);
      setNewLiveItems([]);
      setHasLatestItems(false);
    }
  };

  if (blockState.isLoading || !appPref?.isAppActive || !isInitialLoadComplete) {
    const loaders = Array.from({ length: 8 });
    return (
      <IonContent>
        {loaders.map((_, index) => (
          <Skeleton key={index} />
        ))}
      </IonContent>
    );
  }

  return (
    <IonContent
      scrollEvents={true}
      ref={contentRef}
      onIonScroll={resetFlagOnScrollingTop}
    >
      {hasLatestItems && (
        <IonFab
          vertical="top"
          horizontal="center"
          slot="fixed"
          style={{ marginTop: "10px" }}
        >
          <IonButton
            color="primary"
            className="custom_cmed_mobile_button"
            onClick={handlePopupClick}
            style={{
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              "--border-radius": "50px",
            }}
          >
            NEW TRADE(S) HAVE POSTED
            <IonIcon slot="end" icon={arrowUp} />
          </IonButton>
        </IonFab>
      )}
      <div className="bf__container">
        {blockState.filteredItems.length > 0 ? (
          blockState.filteredItems.map((item, index) => (
            <Block item={item} key={`${item.id}-${index}`} />
          ))
        ) : (
          <div className="empty-state-wrapper">
            <div className="empty-state-container">
              <IonNote
                color="medium"
                style={{ fontSize: "var(--one-font-size-10)" }}
              >
                There are no results based on your selected filters.
              </IonNote>
            </div>
          </div>
        )}

        <IonInfiniteScroll
          onIonInfinite={(event) => {
            getBlocksForTimestamp(event);
          }}
        >
          <IonInfiniteScrollContent />
        </IonInfiniteScroll>
      </div>
    </IonContent>
  );
};

export default DetailedBlockFeed;
